import React from 'react';
import PillBadge from './PillBadge';
import { planMeta, subscriptionStatusLabel, subscriptionStatusColor } from '../utils/profile';
import { formatPrice, formatDate } from '../../utils/format';
import { canShowPurchaseCta } from '../utils/storeCompliance';

export default function SubscriptionRow({
  plan,
  planStatus,
  cancelAtPeriodEnd = false,
  currentPeriodEnd = null,
  onManage,
  onSubscribe,
}) {
  const meta = planMeta(plan);
  const hasPlan = !!meta.id;
  // Une résiliation programmée laisse plan_status à 'active' côté Stripe : sans
  // ce cas particulier, l'app afficherait « Actif » à un abonné qui a résilié.
  const statusVariant = cancelAtPeriodEnd ? 'warning' : subscriptionStatusColor(planStatus);
  const statusText = cancelAtPeriodEnd ? 'Résiliation en cours' : subscriptionStatusLabel(planStatus);
  const endLabel = currentPeriodEnd ? formatDate(currentPeriodEnd, 'fr') : null;

  if (!hasPlan) {
    return (
      <div style={styles.empty}>
        <span style={styles.emptyEmoji} aria-hidden>💳</span>
        <div style={styles.emptyBody}>
          <div style={styles.emptyTitle}>Aucun abonnement actif</div>
          {/* ⚠️ App Store 3.1.3(f) : sur iOS, ni incitation ni bouton menant à
              un achat hors de l'app. On se limite au constat. */}
          <p style={styles.emptyText}>
            {canShowPurchaseCta()
              ? 'Découvrez les plans Revyo pour activer toutes les fonctionnalités.'
              : 'Votre compte n’a pas d’abonnement actif.'}
          </p>
        </div>
        {canShowPurchaseCta() && (
          <button type="button" onClick={onSubscribe} style={styles.primary}>
            Voir les plans →
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={styles.row}>
      <div style={styles.head}>
        <div style={styles.left}>
          <span style={styles.planName}>{meta.name}</span>
          {/* formatPrice : « 9,99 € » et non « 9.99€ ». Vue mobile FR. */}
          <span style={styles.price}>{formatPrice(meta.price, 'fr')}/mois</span>
        </div>
        <PillBadge label={statusText} variant={statusVariant} />
      </div>
      <ul style={styles.features}>
        {meta.features.slice(0, 3).map((f, i) => (
          <li key={i} style={styles.feature}>
            <span style={styles.bullet} aria-hidden />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {/* État de compte, PAS un appel à l'action : aucun bouton, aucun lien.
          Affiché aussi sur iOS, au même titre que le prix de la formule en
          cours — l'utilisateur a le droit de savoir jusqu'à quand il a accès. */}
      {cancelAtPeriodEnd && (
        <div style={styles.cancelNotice}>
          {endLabel
            ? `Votre abonnement prend fin le ${endLabel}. Vous gardez l'accès jusqu'à cette date.`
            : "Votre abonnement prend fin à l'issue de la période déjà payée. Vous gardez l'accès jusque-là."}
        </div>
      )}

      {/* « Gérer mon abonnement » ouvre le portail de facturation Stripe :
          c'est un parcours d'achat hors de l'app, donc masqué sur iOS.
          Le prix de la formule EN COURS reste affiché : c'est l'état du
          compte, pas une incitation à acheter. */}
      {canShowPurchaseCta() && (
        <button type="button" onClick={onManage} style={styles.secondary}>
          Gérer mon abonnement →
        </button>
      )}
    </div>
  );
}

const styles = {
  cancelNotice: {
    fontSize: 13,
    lineHeight: 1.5,
    color: '#92400E',
    background: 'rgba(245, 158, 11, 0.10)',
    border: '1px solid rgba(245, 158, 11, 0.30)',
    borderRadius: 10,
    padding: '10px 12px',
  },
  row: {
    padding: '4px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  planName: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  price: {
    fontSize: 13,
    color: '#6B6B6B',
    fontWeight: 600,
  },
  features: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  feature: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    fontSize: 13,
    color: '#1A1A1A',
  },
  bullet: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#E8502A',
    marginTop: 7,
    flexShrink: 0,
  },
  primary: {
    background: '#E8502A',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 999,
    padding: '10px 16px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  secondary: {
    background: 'transparent',
    color: '#E8502A',
    border: 'none',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    padding: 4,
    fontFamily: 'inherit',
    textAlign: 'left',
    alignSelf: 'flex-start',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '12px 4px',
    textAlign: 'center',
  },
  emptyEmoji: {
    fontSize: 36,
    lineHeight: 1,
  },
  emptyBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  emptyTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  emptyText: {
    margin: 0,
    fontSize: 13,
    color: '#6B6B6B',
    maxWidth: 260,
  },
};
